import { useMutation } from "@tanstack/react-query";
import { GatewayService } from "../api/gateway.service";
import type { GoogleRevocation } from "../api/gateway.service";

/** State and action returned by {@link useDisconnectGoogleAccount}. */
export interface UseDisconnectGoogleAccountReturn {
    /**
     * Revoke the current user's Google grant and delete the stored tokens.
     *
     * Resolves to both facts: whether tokens were deleted, and whether Google
     * confirmed the grant is gone.
     */
    disconnectGoogleAccount: () => Promise<GoogleRevocation>;
    loading: boolean;
    error: Error | null;
}

/**
 * Disconnect the current user's Google account.
 *
 * No query invalidation: nothing cached mirrors the Google grant, and the toolbox
 * session is deliberately left open by the endpoint, so the user stays logged in.
 * The Drive and Sheets features will ask for a fresh authorization on their next
 * use, which is the point.
 */
export const useDisconnectGoogleAccount = (): UseDisconnectGoogleAccountReturn => {
    const mutation = useMutation({
        mutationFn: () => GatewayService.disconnectGoogleAccount(),
    });
    return {
        disconnectGoogleAccount: () => mutation.mutateAsync(),
        loading: mutation.isPending,
        error: mutation.error,
    };
};
